/**
 * SEC-002 (2026-07-06) — Download-token helper.
 *
 * The `/api/files` endpoint no longer accepts `?auth=<session_jwt>`. `<img src>`
 * consumers must first mint a 60-second HMAC token via
 * `GET /api/files/download-token?path=...` (Bearer header required), then use
 * `?dtoken=<...>` in the image URL.
 *
 * This module owns:
 *   1. `fetchDownloadUrl(path, token, apiBase)` — imperative fetch + cache
 *   2. `useDownloadUrl(path)` — React hook (uses AuthContext for token)
 *
 * Cache TTL is 50s (10s safety margin under the backend's 60s dtoken exp).
 * Cache is scoped per (userToken, path) to prevent leaking a URL minted for
 * one user to a session that swapped identity.
 */
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// In-memory dtoken cache: key = `${bearerSuffix}|${path}`  →  {url, expiresAt}
// Keyed on last 12 chars of the Bearer token so different sessions never
// share cached URLs. Never persisted to localStorage.
const _cache = new Map();
const CACHE_TTL_MS = 50 * 1000;

function _cacheKey(token, path) {
  const suffix = (token || '').slice(-12);
  return `${suffix}|${path}`;
}

/**
 * Mint (or reuse cached) dtoken-bearing image URL for `path`.
 *
 * @param {string} path — storage_path (e.g. "cinebreak/users/{uid}/x.png")
 * @param {string} token — user session JWT
 * @param {string} [apiBase] — override for API base (defaults to env-derived)
 * @returns {Promise<string>} full URL usable as <img src>
 * @throws  when path is empty or the mint call fails
 */
export async function fetchDownloadUrl(path, token, apiBase = API_BASE) {
  if (!path) throw new Error('fetchDownloadUrl: path is required');
  if (!token) throw new Error('fetchDownloadUrl: token is required');

  const key = _cacheKey(token, path);
  const cached = _cache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.url;

  const res = await fetch(
    `${apiBase}/files/download-token?path=${encodeURIComponent(path)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) {
    throw new Error(`download-token mint failed: HTTP ${res.status}`);
  }
  const body = await res.json();
  const dtoken = body.dtoken || body.token;
  if (!dtoken) throw new Error('download-token response missing dtoken field');

  const url = `${apiBase}/files?path=${encodeURIComponent(path)}&dtoken=${dtoken}`;
  _cache.set(key, { url, expiresAt: Date.now() + CACHE_TTL_MS });
  return url;
}

/**
 * React hook — resolves an authenticated image URL for `path`.
 *
 * Returns `{ url, loading, error }`. `url` is `null` until the dtoken lands.
 * Re-fetches when `path` changes; cache hit is synchronous on subsequent renders.
 *
 * Usage:
 *   const { url } = useDownloadUrl(photo.storage_path);
 *   return <img src={url || ''} alt="..." />;
 */
export function useDownloadUrl(path) {
  const { token } = useAuth();
  const [state, setState] = useState({ url: null, loading: Boolean(path), error: null });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!path || !token) {
      setState({ url: null, loading: false, error: null });
      return;
    }
    // Fast path: cache hit resolves synchronously on next tick.
    const key = _cacheKey(token, path);
    const cached = _cache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
      setState({ url: cached.url, loading: false, error: null });
      return;
    }
    setState({ url: null, loading: true, error: null });
    fetchDownloadUrl(path, token)
      .then((url) => { if (mounted.current) setState({ url, loading: false, error: null }); })
      .catch((err) => { if (mounted.current) setState({ url: null, loading: false, error: err }); });
  }, [path, token]);

  return state;
}

// Escape hatch — used only by tests / diagnostics.
export function _clearDtokenCache() { _cache.clear(); }
